/**
 * Money is integer cents. Gold is a two-decimal fraction.
 *
 * Two currencies with different rules meet in this app, so the split is worth
 * stating once: USD never leaves integer minor units, because a parent settles
 * it in cash and a tenth of a cent is not a thing they can hand over. Gold
 * arrives from Cleanify already rounded to two decimals (`roundGold` in
 * `src/domain/economy/scoring.js`) and stays that way, because re-rounding it
 * here would make Fortunaut's copy of a balance disagree with the one on the
 * Cleanify screen next to it.
 */

/** Gold rounds to two decimals — same rule as Cleanify's `roundGold`. */
export const roundGold = (amount) => Math.round((Number(amount) || 0) * 100) / 100;

/**
 * Gold as a child reads it: whole coins they actually have.
 *
 * Floored, not rounded, for the reason Cleanify already documented — showing 10
 * for a balance of 9.6 and then refusing a 10-gold reward is a small betrayal
 * that costs more trust than the rounding saves.
 */
export const displayGold = (amount) => Math.floor(roundGold(amount));

export const centsFromDollars = (dollars) => Math.round((Number(dollars) || 0) * 100);

/**
 * Grouped thousands, because the numbers that most need reading are the big
 * ones. "$3120.00" is the annual cost of a weekly envelope and the figure a
 * parent is supposed to pause on; unseparated, it reads as noise and gets
 * skimmed. Deliberately not `Intl.NumberFormat` — this module is USD-only by
 * design (see the header) and stays zero-dependency and locale-free so the
 * same call renders identically in the app, the two published pages, and a
 * test.
 */
export function formatCents(cents, { sign = false } = {}) {
  const n = Math.trunc(Number(cents) || 0);
  const [whole, frac] = (Math.abs(n) / 100).toFixed(2).split('.');
  const body = `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${frac}`;
  if (n < 0) return `-${body}`;
  return sign && n > 0 ? `+${body}` : body;
}

/**
 * Split `cents` across `weights`, giving the remainder away one cent at a time.
 *
 * A child allocating $10 three ways must still have $10 afterwards. Rounding
 * each share independently loses or invents a cent, and a ledger that invents a
 * cent every Tuesday stops reconciling by Christmas. Largest-remainder keeps the
 * sum exact and hands the odd cents to the buckets that were closest to earning
 * them anyway.
 */
export function allocate(cents, weights) {
  const total = weights.reduce((sum, w) => sum + w, 0);
  if (total <= 0 || cents === 0) return weights.map(() => 0);

  const exact = weights.map((w) => (cents * w) / total);
  const shares = exact.map(Math.floor);
  let remainder = cents - shares.reduce((sum, s) => sum + s, 0);

  const order = exact
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac);

  for (let i = 0; remainder > 0; i += 1, remainder -= 1) {
    shares[order[i % order.length].index] += 1;
  }
  return shares;
}
